import { useEffect, useState } from 'react';
import { api } from '../lib/api';

/**
 * A child's own house, on the family portal.
 *
 * ⚠ THIS IS THE HALF OF HOUSE POINTS THAT NEVER EXISTED. Every ledger row has carried a StudentId
 * since the feature was written and nothing has ever read it. The board could say Sahyadri has 344
 * points and could not say that Meghana earned six of them — which is the only part a ten-year-old
 * cares about, and the entire reason a house system motivates anybody.
 *
 * ⚠ IT DOES NOT RANK CHILDREN, and that is deliberate. It shows what you contributed and where your
 * HOUSE stands. It never says you are fourth-best in Sahyadri. Same rule the report card follows —
 * no rank on anything a student sees. A house competition exists to make a quiet child feel part of
 * something; a leaderboard of individuals does the exact opposite, and would turn two points for a
 * kind remark into a reason to feel behind.
 *
 * ⚠ EVERY LINE IS SOMETHING THEY DID WELL, structurally rather than by filtering. The domain
 * refuses to attach a student to a deduction at all, so this list cannot contain a minus however it
 * is queried. There is no filter here to forget.
 */

interface Contribution { points: number; source: string; reason: string; on: string; }
interface MyHouse {
  houseId: string; houseName: string; colorHex: string; motto: string | null;
  academicYear: string;
  housePoints: number; housePosition: number; houseIsLeading: boolean;
  myPoints: number;
  contributions: Contribution[];
}

const ORDINAL = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
};

export function MyHouseCard({ studentId }: { studentId: string }) {
  const [h, setH] = useState<MyHouse | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get<MyHouse | null>(`/api/portal/students/${studentId}/house`)
      .then((r) => { if (alive) setH(r); })
      .catch(() => { if (alive) setH(null); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [studentId]);

  /**
   * ⚠ Nothing at all when the school runs no houses. A card saying "no house" is a card telling a
   * family their child was left out of something, when the school simply does not do houses.
   *
   * ⚠ AND IT CHECKS A FIELD, NOT TRUTHINESS. `!h` was not enough and it threw on the first render
   * I looked at: an endpoint that answers `{}` — a stub, a proxy, a misconfigured gateway, a future
   * version that returns an empty object instead of null — is a TRUTHY object with no
   * `contributions` array on it, so the guard passed and the render crashed reading `.length`.
   * Asking for the one field this component cannot work without is the honest check.
   */
  if (!loaded || !h?.houseId || !Array.isArray(h.contributions)) return null;

  return (
    <div className="card myhouse" style={{ borderLeft: `6px solid ${h.colorHex}`, padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 18, color: h.colorHex }}>{h.houseName}</span>
        {h.motto && <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{h.motto}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>{h.academicYear}</span>
      </div>

      <div style={{ display: 'flex', gap: 26, marginTop: 12, flexWrap: 'wrap' }}>
        <div>
          {/* ⚠ THE CHILD'S OWN NUMBER FIRST. It is the smaller figure and the one that is theirs. */}
          <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>{h.myPoints}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
            {h.myPoints === 1 ? 'point you earned' : 'points you earned'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, color: h.colorHex }}>{h.housePoints}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
            {/* The HOUSE's position — a team result, never the child's. */}
            {h.houseIsLeading ? 'and your house is leading' : `and your house is ${ORDINAL(h.housePosition)}`}
          </div>
        </div>
      </div>

      {h.contributions.length > 0 && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--line, #e6e8ec)', paddingTop: 10 }}>
          {h.contributions.slice(0, 6).map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, fontSize: 13, padding: '4px 0' }}>
              <span style={{ fontWeight: 700, color: h.colorHex, minWidth: 26 }}>+{c.points}</span>
              <span style={{ flex: 1 }}>{c.reason}</span>
              <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                {new Date(c.on).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ⚠ A child who has earned nothing yet is told the house is theirs anyway — not shown an
          empty list that reads as a scoreboard they are losing on. */}
      {h.contributions.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 12, marginBottom: 0 }}>
          Every point {h.houseName} earns this year is yours too.
        </p>
      )}
    </div>
  );
}
